import type { HTMLAttributes } from "react";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import type { Amount } from "../../../../../shared/core/amount/amount";
import { Config } from "../../../../../shared/core/config/config";
import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import { featuresManager } from "../../../../../shared/core/service/services";
import type { Account } from "../../../../../shared/domains/account/account";
import { AccountingEntryType } from "../../../../../shared/domains/accounting-transaction/accounting-entry";
import type { AccountingTransaction } from "../../../../../shared/domains/accounting-transaction/transaction/transaction";
import { useSpendings } from "../../../../../shared/domains/accounting-transaction/transaction/use-spendings";
import { useTransactionLinks } from "../../../../../shared/domains/accounting-transaction/use-transaction-links";
import useCurrencies from "../../../../../shared/domains/currencies/use-currencies";
import { useObservable } from "../../../../../shared/utils/observable";
import { getTotalOfDetailedAccounting } from "../../../../../shared/utils/total-amount";
import {
  ACCOUNT_TRANSACTION_DETAILS_MODAL_ID,
  REFUND_TRANSACTION_REASON_MODAL_ID,
} from "../../../../core/modal/modal-id";
import { Path } from "../../../../core/routing/path";
import { useClient } from "../../../../domain/authentication/use-client";
import { useRTL } from "../../../../domain/language/use-rtl";
import { AmountText } from "../../../common/amount-text";
import { BluePrimaryButton, GrayPrimaryButton } from "../../../common/buttons/primary-button";
import { MapIframe } from "../../../common/map-iframe";
import { Modal } from "../../../common/modal/modal";
import { WhiteModalContainer } from "../../../common/modal/white-modal-container";
import { TransactionValidateIcon } from "../../../common/svg/transaction-validate-icon";
import { theme } from "../../../styles/theme";
import { RefundTransactionReasonModal } from "./refund-transaction-reason-modal";
import { TransactionStateTag } from "./transaction-state-tag";

interface AccountTransactionDetailsProps {
  transaction: AccountingTransaction;
  account: Account;
}

const accountEntryLabels = {
  [AccountingEntryType.Transfer]: "accountingTransactionDetailsModal.transferLabel",
  [AccountingEntryType.Fee]: "accountingTransactionDetailsModal.feeLabel",
  [AccountingEntryType.Commission]: "accountingTransactionDetailsModal.commissionLabel",
  [AccountingEntryType.Tax]: "accountingTransactionDetailsModal.taxLabel",
  [AccountingEntryType.Discount]: "accountingTransactionDetailsModal.discountLabel",
  [AccountingEntryType.Interest]: "accountingTransactionDetailsModal.interestLabel",
};

export const AccountTransactionDetailsModal = (props: AccountTransactionDetailsProps) => (
  <WhiteModalContainer closeButton id={ACCOUNT_TRANSACTION_DETAILS_MODAL_ID}>
    <AccountTransactionDetails transaction={props.transaction} account={props.account} />
  </WhiteModalContainer>
);

const AccountTransactionDetails: React.FC<AccountTransactionDetailsProps> = ({ transaction, account }) => {
  const { formatMessage, formatDate } = useIntl();
  const { canJustify, canEditJustification, canViewJustification, isOutgoing } = useTransactionLinks();
  const { push } = useHistory();
  const { client } = useClient();
  const { amountJustified, spendings } = useSpendings(transaction);

  const features = useObservable(featuresManager.features);

  const detailedAccountingTransfer = transaction?.detailedAccounting?.find(
    (accounting) => accounting.type === AccountingEntryType.Transfer,
  );
  const transactionDescription =
    transaction.shortDescription || detailedAccountingTransfer?.description || transaction.type.label;
  const additionalInformation = useMemo(() => {
    let externalDataValue: string | undefined;
    let externalId: string | undefined;
    if (transaction) {
      externalId = transaction.externalId;
      if (transaction.externalData) {
        const externalData = transaction.externalData;
        externalDataValue = Object.keys(externalData)
          .map((key) => `${key} : ${externalData[key]}`)
          .join("\n");
      }
      if (transaction.additional) {
        externalDataValue = externalDataValue
          ? externalDataValue + "\n" + transaction.additional
          : transaction.additional;
      }
    }
    return { externalId: externalId, externalData: externalDataValue };
  }, [transaction]);
  const remittanceInformation = transaction.externalData?.remittanceInformation;

  if (!transaction.amount) {
    return null;
  }

  const handleJustifyTransaction = () => {
    push(Path.JustifyTransaction, { transaction, account });
    Modal.dismiss(ACCOUNT_TRANSACTION_DETAILS_MODAL_ID);
  };

  /**
   * This is a temporary solution to hide the refund button.
   * The feature must be hidden until the api handles this type of request (by implementing a step of bank validation)
   * Original ticket: https://tagpay.atlassian.net/browse/VC-563
   * Dev Note: TODO remove this const variable when the feature is enabled.
   */
  const displayRefundButton = false;

  return (
    <Container>
      <Header>
        <Title>{transactionDescription}</Title>
        {isOutgoing(transaction) && (
          <Tags>
            <TransactionStateTag
              label={formatMessage(
                transaction.justified ? "accountTransactions.tagJustified" : "accountTransactions.tagNotJustified",
              )}
              done={!!transaction.justified}
            />
            <TransactionStateTag
              label={formatMessage(
                transaction.qualified ? "accountTransactions.tagQualified" : "accountTransactions.tagNotQualified",
              )}
              done={!!transaction.qualified}
            />
          </Tags>
        )}
      </Header>
      {!!transaction.id && (
        <Reference>{formatMessage("accountingTransactionDetailsModal.transaction", { id: transaction.id })}</Reference>
      )}
      {/* The mockup keeps the number alone; the date stays, quieter, rather than being lost. */}
      <Reference>
        {formatDate(transaction.date, {
          month: "long",
          day: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </Reference>
      {!!transaction.description && <Description>{transaction.description}</Description>}
      {!!transaction.label && (
        <Description>
          {formatMessage("accountingTransactionDetailsModal.description", { description: transaction.label })}
        </Description>
      )}
      {remittanceInformation && (
        <Description>
          {formatMessage("accountingTransactionDetailsModal.reference", { id: remittanceInformation })}
        </Description>
      )}
      <DetailsTitle>{formatMessage("accountingTransactionDetailsModal.detailsTitle")}</DetailsTitle>
      <Items>
        {transaction.detailedAccounting.map((value, index) => (
          <TransactionEntryView
            key={value.id}
            label={
              index === 0
                ? formatMessage(accountEntryLabels[transaction.detailedAccounting[0]?.type])
                : formatMessage(accountEntryLabels[value.type])
            }
            amount={{ value: value.amount.value, currency: transaction.amount.currency }}
            bold={transaction.detailedAccounting.length === 1}
            showExchangeRate={transaction.detailedAccounting.length === 1}
            hasLink
          />
        ))}
        {(!transaction.detailedAccounting || transaction.detailedAccounting.length > 1) && (
          <TransactionEntryView
            label={formatMessage("accountingTransactionDetailsModal.amountLabel")}
            amount={{
              value: getTotalOfDetailedAccounting(transaction.detailedAccounting),
              currency: transaction.amount.currency,
            }}
            hasLink
            bold
            showExchangeRate
          />
        )}
        <TransactionEntryView
          label={formatMessage("accountingTransactionDetailsModal.balanceLabel")}
          amount={transaction.balance}
          isBalance
        />
      </Items>
      {spendings.length > 0 ? (
        <>
          <DetailsTitle>{formatMessage("accountingTransactionDetailsModal.justificationFollowUpTitle")}</DetailsTitle>
          <Items>
            <TransactionEntryView
              label={formatMessage("accountingTransactionDetailsModal.documentsSubmitted")}
              amount={amountJustified()}
              hasLink={canEditJustification(transaction)}
              bold
            />
            {canEditJustification(transaction) ? (
              <>
                <TransactionEntryView
                  label={formatMessage("accountingTransactionDetailsModal.validationInProgress")}
                  amount={null}
                  grey
                />
              </>
            ) : null}
          </Items>
        </>
      ) : null}
      {Config.APP_ACCOUNTING_TRANSACTION_DETAILS_DISPLAY_ADDITIONAL_INFO &&
      (additionalInformation.externalId || additionalInformation.externalData) ? (
        <>
          <DetailsTitle>{formatMessage("accountingTransactionDetailsModal.additionalInformationTitle")}</DetailsTitle>
          {additionalInformation.externalData ? <Description>{additionalInformation.externalData}</Description> : null}
          {additionalInformation.externalId ? (
            <ExternalId>
              {formatMessage("accountingTransactionDetailsModal.externalIdLabel", {
                externalId: additionalInformation.externalId,
              })}
            </ExternalId>
          ) : null}
        </>
      ) : null}
      {transaction.geolocation && (
        <>
          <Location>{formatMessage("accountingTransactionDetailsModal.geolocationTitle")}</Location>
          <MapIframe location={transaction.geolocation} allowFullScreen />
        </>
      )}
      {displayRefundButton &&
        transaction.businessProcessId &&
        transaction.type?.value === "ACCOUNT_TO_EXTERNAL" &&
        features.customerInstructionSubmit &&
        features.customerInstructionView &&
        features.paymentInstructionView && (
          <CanRefundButton
            onClick={() => {
              Modal.present(
                REFUND_TRANSACTION_REASON_MODAL_ID,
                () => <RefundTransactionReasonModal transaction={transaction} client={client} />,
                {
                  canBeDismissed: false,
                },
              );
            }}
            size="S"
          >
            {formatMessage("accountingTransactionDetailsModal.refundButtonLabel")}
          </CanRefundButton>
        )}
      {(canViewJustification(transaction) || canJustify(transaction)) && (
        <CanJustifyButton onClick={handleJustifyTransaction} size="XS">
          {formatMessage(
            canJustify(transaction)
              ? "accountingTransactionDetailsModal.justifyButtonLabel"
              : "accountingTransactionDetailsModal.verifyJustificationsButtonLabel",
          )}
        </CanJustifyButton>
      )}
    </Container>
  );
};

interface TransactionEntryViewProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  amount: Amount | undefined;
  /** The line the timeline ends on: the balance the transaction leaves behind. */
  isBalance?: boolean;
  grey?: boolean;
  hasLink?: boolean;
  bold?: boolean;
  showExchangeRate?: boolean;
}

export const TransactionEntryView: React.FC<TransactionEntryViewProps> = (props) => {
  const { label, amount, isBalance, grey, hasLink, bold, showExchangeRate, ...viewProps } = props;
  const { formatMessage } = useIntl();
  const { isRTL } = useRTL();
  const { canShowExchangeRate, convertedAmount } = useCurrencies(
    amount,
    Config.CURRENCY_TO_CONVERT_IN_EUR_ENABLED ? "EUR" : null,
  );

  return (
    <Item {...viewProps}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <BulletSlot $isRTL={isRTL}>
          {isBalance || grey ? (
            <Bullet grey={grey} />
          ) : (
            <TransactionValidateIcon width={16} height={16} fill={theme.colors.success[500]} />
          )}
        </BulletSlot>
        <div>
          <ItemLabel grey={grey} bold={bold}>
            {label}
          </ItemLabel>
          {canShowExchangeRate && showExchangeRate && convertedAmount && (
            <ItemLabel style={{ ...theme.bodyGreyRegular }}>
              {formatMessage("amountSelection.convertedAmountLabel")}
            </ItemLabel>
          )}
        </div>
      </div>
      {amount !== null ? (
        <ItemValue bold={bold}>
          <div
            style={{
              textAlign: "right",
            }}
          >
            {amount && <AmountText amount={amount} />}
            {canShowExchangeRate && showExchangeRate && convertedAmount && (
              <AmountText
                style={{
                  ...theme.bodyGreyRegular,
                }}
                amount={convertedAmount}
              />
            )}
          </div>
        </ItemValue>
      ) : null}
      {hasLink && <Link $isRTL={isRTL} />}
    </Item>
  );
};

/* The cross now sits in the top right corner, where the tags are: they start below it. */
const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 40px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Title = styled.h2`
  ${theme.boldText};
  margin: 0;
  flex: 1 1 auto;

  /* A label carrying no space still has to give way, or it pushes the tags out. */
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: 20px;
  line-height: 26px;
  color: ${theme.colors.blue[800]};
`;

const Tags = styled.div`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  gap: 8px;
`;

const DetailsTitle = styled.h3`
  ${theme.mediumText};
  margin: 28px 0 12px;
  font-size: 13px;
  line-height: 17px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${theme.colors.blue[800]};
`;

const Reference = styled.span`
  ${theme.text};
  margin-top: 4px;
  font-size: 13px;
  line-height: 18px;
  color: ${theme.colors.gray[500]};
`;

const Description = styled.span`
  ${theme.text};
  margin-top: 8px;

  /* The additional data comes joined by newlines; they have to survive. */
  white-space: pre-line;
  font-size: 13px;
  line-height: 18px;
  color: ${theme.colors.gray[800]};
`;

const ExternalId = styled.span`
  ${theme.text}
`;

const Item = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;

const ItemLabel = styled.span<{ bold?: boolean; green?: boolean; grey?: boolean }>`
  ${(props) => (props.bold ? theme.boldText : theme.text)};
  display: block;
  font-size: 15px;
  color: ${(props) => (props.grey ? "#b1b1b1" : theme.colors.blue[800])};
  flex-shrink: 1;
  flex-grow: 1;
`;

const ItemValue = styled.span<{ bold?: boolean }>`
  ${theme.boldText};
  margin-left: 20px;
  font-size: 15px;
  color: ${theme.colors.blue[800]};
`;

const BulletSlot = styled.span<{ $isRTL: boolean }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-right: ${(props) => (props.$isRTL ? 0 : 12)}px;
  margin-left: ${(props) => (props.$isRTL ? 12 : 0)}px;
`;

/* The balance the line ends on: a dark centre inside a ring, as the mockup draws it. */
const Bullet = styled.span<{ grey?: boolean }>`
  box-sizing: border-box;
  width: 16px;
  height: 16px;
  border: 3px solid #ffffff;
  border-radius: 50%;
  background-color: ${(props) => (props.grey ? "#b1b1b1" : theme.colors.blue[800])};
  box-shadow: 0 0 0 1.5px ${(props) => (props.grey ? "#b1b1b1" : theme.colors.blue[800])};
`;

const Items = styled.div`
  margin-bottom: 4px;

  > *:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const Link = styled.div<{ $isRTL: boolean }>`
  position: absolute;
  top: 100%;
  left: ${(props) => (props.$isRTL ? "unset" : "7px")};
  right: ${(props) => (props.$isRTL ? "7px" : "unset")};
  width: 2px;
  height: 20px;
  border-radius: 1.5px;
  background-color: ${theme.colors.success[500]};
`;

const Location = styled(DetailsTitle)`
  margin-top: 30px;
`;

const CanJustifyButton = styled(BluePrimaryButton)`
  align-self: center;
  margin-top: 28px;
  padding: 13px 28px;
  box-shadow: none;
`;

const CanRefundButton = styled(GrayPrimaryButton)`
  margin-top: 35px;
`;
